import React, { RefObject, useEffect } from 'react';
import {
  Clear,
  Close,
  Search,
  Search as SearchIcon
} from '@mui/icons-material';
import {
  Breakpoint,
  IconButton,
  SxProps,
  Theme,
  useMediaQuery
} from '@mui/material';
import {
  bindPopover,
  bindTrigger,
  usePopupState
} from 'material-ui-popup-state/hooks';

import {
  StyledPopover,
  StyledSearchButton,
  StyledTextField
} from '@/components/GridToolbarQuickFilter';

interface SearchBar {
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  popoverBreakpoint?: Breakpoint;
  placeholder?: string;
  sx?: SxProps<Theme>;
  autoFocus?: boolean;
  autoFocusDependencies?: any[];
  color?: 'primary' | 'secondary';
  forceDesktopVariant?: boolean;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  inputRef?: RefObject<HTMLInputElement>;
}

export const SearchBar: React.FC<SearchBar> = ({
  searchQuery,
  popoverBreakpoint = 'xl',
  setSearchQuery,
  placeholder = 'Search',
  sx,
  autoFocus = false,
  autoFocusDependencies,
  color = 'primary',
  forceDesktopVariant = false,
  onKeyDown,
  inputRef
}) => {
  const [focused, setFocused] = React.useState(false);
  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'quickFilterPopover'
  });

  const localInputRef = inputRef || React.createRef<HTMLInputElement>();

  React.useEffect(() => {
    if (popupState.isOpen) {
      setTimeout(() => localInputRef.current?.focus(), 50);
    }
  }, [popupState, localInputRef]);

  const handleSubmit = (e: React.MouseEvent | React.FormEvent) => {
    e.preventDefault();
    popupState.close();
  };
  const handleClose = () => {
    setSearchQuery('');
    popupState.close();
  };
  const isMediaQueryDownBreakpoint = forceDesktopVariant
    ? false
    : useMediaQuery((theme: Theme) =>
        theme.breakpoints.down(popoverBreakpoint)
      );

  const handleAutoFocus = () => {
    localInputRef?.current?.focus();
  };

  useEffect(() => {
    if (localInputRef && autoFocus) {
      handleAutoFocus();
    }
  }, []);

  useEffect(() => {
    if (autoFocusDependencies?.length) {
      handleAutoFocus();
    }
  }, [...(autoFocusDependencies || [])]);

  const bindedTextField = (
    <StyledTextField
      sx={sx}
      className={focused ? 'focused' : ''}
      value={searchQuery}
      onChange={(e) => setSearchQuery(e.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onKeyDown={onKeyDown}
      variant="standard"
      inputRef={localInputRef}
      InputProps={{
        id: 'search-bar',
        startAdornment: (
          <SearchIcon
            htmlColor={color === 'secondary' ? '#667A86' : '#4E616D'}
            sx={{
              marginRight: '8px',
              height: '0.8em',
              width: '0.8em'
            }}
          />
        ),
        endAdornment: searchQuery.length > 0 && (
          <Clear
            sx={{
              width: '18px',
              height: '18px',
              color: '#9AAEBB',
              cursor: 'pointer'
            }}
            onClick={() => setSearchQuery('')}
          />
        ),
        autoCapitalize: 'off',
        autoCorrect: 'off',
        autoComplete: 'off',
        disableUnderline: true,
        placeholder: placeholder
      }}
    />
  );

  return isMediaQueryDownBreakpoint ? (
    <>
      <StyledSearchButton
        data-testid="search-bar-button"
        color={color}
        hasSearchQuery={searchQuery.length > 0 ? true : false}
        {...bindTrigger(popupState)}
      >
        <Search
          fontSize="small"
          color="secondary"
          htmlColor={color === 'secondary' ? '#667A86' : undefined}
        />
      </StyledSearchButton>
      <StyledPopover {...bindPopover(popupState)}>
        <form onSubmit={handleSubmit}>{bindedTextField}</form>
        <IconButton onClick={handleClose} size="small">
          <Close htmlColor="#9AAEBB" />
        </IconButton>
      </StyledPopover>
    </>
  ) : (
    bindedTextField
  );
};
